import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import InputWithLabel from '@ui-parts/InputWithLabel';
import styled from 'styled-components';
import Button from '@ui-elements/Button';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '~/constants/routes';
import { PALETTE, FONT_SIZES, Z_INDEX } from '~/assets/styles/base';
import { passwordResetRequestUser, callThunk } from '~/api';
import { initialize } from '~/features/passwordReset/passwordResetSlice';
import { emailValidationRule } from '~/constants/validationRules';

const PasswordRegenerateContainer = styled.div`
  max-width: 720px;
  border: 1px solid ${PALETTE.border.frame};
  margin: 50px auto;
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 45px;
  background: ${PALETTE.white};
  text-align: center;
  white-space: pre-line;
`;

const Title = styled(Typography)`
  color: ${PALETTE.primary.main};
  padding-top: 10px;
  padding-bottom: 10px;
  font-size: ${FONT_SIZES.body1};
`;

const Message = styled(Typography)`
  font-size: ${FONT_SIZES.title2};
  // padding-top: 10px;
`;

const EmailInputWrapper = styled.div`
  width: 453px;
  margin: 0 auto;
  text-align: left;
  padding-top: 20px;
  padding-bottom: 30px;
`;

const SendButton = styled(Button)`
  display: block;
  width: 150px;
  margin: 0 auto;
`;

function PasswordResetRequest() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const passwordReset = useSelector((state) => state.passwordReset);
  const { t } = useTranslation();

  const { control, handleSubmit } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data) => {
    dispatch(
      callThunk({
        action: passwordResetRequestUser,
        payload: data,
      })
    );
  };

  // isCompletedフラグを初期化
  useEffect(() => {
    dispatch(initialize());
  }, []);

  useEffect(() => {
    if (passwordReset.isCompleted) {
      navigate(ROUTES.USER.PASSWORD_RESET_SEND_MAIL_COMPLETED);
    }
  }, [passwordReset.isCompleted]);

  return (
    <PasswordRegenerateContainer data-testid="User-PasswordRegenerateContainer">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Wrapper>
          <Title data-testid="User-PasswordRegenerateContainer-Title">
            {t('passwordResetRequest.title')}
          </Title>
          <Message data-testid="User-PasswordRegenerateContainer-Message">
            {t('passwordResetRequest.message')}
          </Message>
          <EmailInputWrapper>
            <Controller
              name="email"
              control={control}
              rules={emailValidationRule}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { error },
              }) => {
                return (
                  <InputWithLabel
                    label={t('passwordResetRequest.email')}
                    name={name}
                    type="text"
                    value={value}
                    errorMessage={error?.message}
                    width="453px"
                    textAlign="left"
                    handleChange={(e) => {
                      onChange(e);
                    }}
                    handleBlur={(e) => {
                      onBlur(e);
                    }}
                    error={Boolean(error)}
                    errorZIndex={Z_INDEX.tooltip}
                    dataTestId="User-PasswordRegenerateContainer-email"
                    inputProps={{ maxLength: 100 }}
                    ref={ref}
                  />
                );
              }}
            />
          </EmailInputWrapper>
          <SendButton
            variant="contained"
            type="submit"
            data-testid="User-PasswordRegenerateContainer-SendButton"
          >
            {t('passwordResetRequest.send')}
          </SendButton>
        </Wrapper>
      </form>
    </PasswordRegenerateContainer>
  );
}

export default PasswordResetRequest;
